import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./callpage.module.css";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canEdit, canDelete } from "../common/permissionUtils";
import calander from "../../assets/svg/calendar.svg";
import deleteIcon from "../../assets/svg/delete.svg";
import editIcon from "../../assets/svg/edit.svg";
import dropdownIcon from "../../assets/svg/edit.svg";
import Dropdown from "../../assets/svg/dropdown.svg";
import { getCallData, getDeleteTeamCallApi, getCallAiSummaryApi } from "./calls-api";
import { getDeleteApi } from "../common/delete-api";
import { useMsal } from "@azure/msal-react";
import { loginRequest, transcriptRequest } from "../../msalConfig";
import { getCrmAiProvidersApi } from "../common/ai-chat-api.js";
import { Spinner } from "react-bootstrap";

const CallsView = () => {
  const [open, setOpen] = useState(false);
  const [callData, setCallData] = useState(null);

  const navigate = useNavigate();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasEditPermission = canEdit(rolePermissionList, "Calls");
  const hasDeletePermission = canDelete(rolePermissionList, "Calls");

  //useLocation
  const location = useLocation();
  const { id } = location.state || {};

  const { instance, accounts } = useMsal();
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const modalStyle = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      opacity: 1,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    modal: {
      backgroundColor: "#ffffff",
      padding: "40px",
      borderRadius: "16px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 1)",
      width: "436px",
      height: "228px",
      textAlign: "center",
    },
  };

  const cancelDelete = () => {
    setIsOpenDelete(false);
    setOpen(false);
  };

  const deleteApi = async () => {
    try {
      const response = await getDeleteApi(`web/call/delete/${callData.id}`);
      if (response?.status === 200 || response?.status === 500) {
        if(response?.data?.success === true) {
           navigate('/calls');
        }
      }
    } catch (err) {
      console.error("Error deleting:", err);
    }
  };

  const deleteTeamCallApi = async () => {
    try {
      instance.loginPopup(loginRequest).then(async (tokenResponse) => {
        const response = await getDeleteTeamCallApi(
          `web/call/delete/${callData.id}`,
          {
            thirdparty: "Microsoft-Teams",
            accesstoken: tokenResponse.accessToken,
          },
        );
        if (response?.status === 200 || response?.status === 500) {
           if(response?.data?.success === true) {
              navigate('/calls');
           }
        }
      });
    } catch (err) {
      console.error("Error deleting team call:", err);
    }
  };

  // AI call summary. The transcript is fetched from Graph once and stored, so
  // reopening a call never re-fetches it and never re-bills the model.
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  // Same picker as the record summaries. The call summary keeps its own card
  // because its output is summary/commitments/next_step, not a score.
  const [aiChoices, setAiChoices] = useState([]);
  const [aiChoice, setAiChoice] = useState("");

  useEffect(() => {
    let live = true;
    (async () => {
      const response = await getCrmAiProvidersApi();
      if (!live || response.data?.success !== true) return;
      const flat = [];
      (response.data.providers ?? []).forEach((p) =>
        (p.models ?? []).forEach((m) => flat.push(`${p.provider}|${m}`)),
      );
      setAiChoices(flat);
      const preferred = (response.data.providers ?? []).find(
        (p) => p.provider === response.data.default,
      );
      setAiChoice(preferred ? `${preferred.provider}|${preferred.model}` : flat[0] ?? "");
    })();
    return () => {
      live = false;
    };
  }, []);

  const ai =
    aiSummary ??
    (callData?.ai_summary
      ? {
          summary: callData.ai_summary,
          commitments: callData.ai_commitments,
          next_step: callData.ai_next_step,
        }
      : null);

  const handleSummariseCall = async () => {
    setAiLoading(true);
    setAiError("");
    try {
      // A separate scope set: reading a transcript needs consent that the
      // everyday Teams flows do not.
      const tokenResponse = await instance.loginPopup(transcriptRequest);
      const [provider, model] = aiChoice.split("|");
      const response = await getCallAiSummaryApi(id, {
        accesstoken: tokenResponse.accessToken,
        ...(provider ? { provider, model } : {}),
      });
      if (response?.data?.success === true) {
        setAiSummary(response.data.data);
      } else {
        setAiError(response?.data?.message || "Could not summarise this call");
      }
    } catch (err) {
      setAiError("Microsoft sign-in was cancelled or failed");
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getCallData(id);
        setCallData(data.call);
      } catch (e) {}
    }
    fetchData();
  }, []);
  console.log(callData);

  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",
        padding: "16px",
        backgroundColor: "#f8f9fb",
      }}
    >
      {isOpenDelete && (
        <div style={modalStyle.overlay} backdrop="static">
          <div style={modalStyle.modal}>
            <div style={{ color: "#ffffff" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#1E1E1E",
                  }}
                >
                  Delete{" "}
                </div>
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "16px",
                    fontWeight: "400",
                    color: "#1E1E1E",
                  }}
                >
                  You won’t able to access your record after deleting. Are you
                  sure want to delete?
                </div>

                <div
                  style={{
                    flex: 12,
                    marginTop: "8px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "start",
                    justifyContent: "start",
                  }}
                >
                  <div
                    onClick={cancelDelete}
                    style={{
                      cursor: "pointer",
                      width: "158px",
                      height: "44px",
                      marginLeft: "8px",
                      marginRight: "8px",
                      borderRadius: "8px",
                      display: "flex",
                      flex: 6,
                      justifyContent: "center",
                      alignItems: "center",
                      border: "1px solid #E5E5E5",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                        color: "#1E1E1E",
                      }}
                    >
                      Cancel
                    </div>
                  </div>
                  <div
                    onClick={() => {
                      if (callData?.teams_id != null && callData?.teams_id !== "") {
                        deleteTeamCallApi();
                      } else {
                        deleteApi();
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      width: "158px",
                      height: "44px",
                      marginLeft: "8px",
                      marginRight: "8px",
                      borderRadius: "8px",
                      display: "flex",
                      flex: 6,
                      justifyContent: "center",
                      alignItems: "center",
                      background: "#FF4444",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                        color: "#FFFFFF",
                      }}
                    >
                      Delete
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={styles.cardWrapper}>
        {/* Header Section */}
        <div className={styles.header}>
          <svg
            className={styles.backIcon}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            onClick={() => navigate(-1)}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          <h1 className={styles.headerTitle}>{callData?.title}</h1>
        </div>

        {/* Main Content Area */}
        <div className={styles.mainContent}>
          {/* Top Section */}
          <div className={styles.topContent}>
            <div className={styles.topSection}>
              <div>
                <h2 className={styles.mainTitle}>{callData?.title}</h2>
                <p className={styles.subText}>{callData?.subject}</p>
                <div className={styles.callTime}>
                  <img
                    className={styles.calendarIcon}
                    src={calander}
                    alt="Calendar Icon"
                  ></img>
                  <span className={styles.callTimeText}>
                    Call starts on{" "}
                    <span className={styles.boldText}>
                      {callData?.time}
                    </span>{" "}
                  </span>
                </div>
              </div>
              <div className={styles.actionButtons} style={{ position: "relative" }}>
                <button className={styles.iconButton} onClick={() => setOpen(!open)}>
                  <svg
                    className={styles.ellipsisIcon}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                    />
                  </svg>
                </button>
                {open && (
                  <div
                    style={{
                      position: "absolute",
                      top: "40px",
                      right: "0",
                      backgroundColor: "#ffffff",
                      padding: "8px",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      zIndex: 1000,
                      width: "120px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px"
                    }}
                  >
                    <div 
                      style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px", color: "#172b4d", padding: "8px", borderRadius: "4px" }} 
                      onClick={() => navigate("/calls/create", { state: { id: callData?.id } })}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4f5f7'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <img src={editIcon} alt="Edit" style={{ width: "16px" }} /> Edit
                    </div>
                    <div 
                      style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px", color: "#FF4444", padding: "8px", borderRadius: "4px" }}
                      onClick={() => setIsOpenDelete(true)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4f5f7'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <img src={deleteIcon} alt="Delete" style={{ width: "16px", filter: "invert(42%) sepia(89%) saturate(1637%) hue-rotate(325deg) brightness(106%) contrast(101%)" }} /> Delete
                    </div>
                  </div>
                )}
              </div>
            </div>

            <hr className={styles.customDashedLine1} />

            {/* Details Section */}
            <div className={styles.detailsSection}>
              {/* Call Details Card */}
              <div className={styles.detailCard_data}>
                <h3 className={styles.cardTitle}>Call details</h3>
                <div className={styles.detailList}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Call type</span>
                    <span className={styles.labeldata}>
                      {callData?.call_type || "N/A"}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Purpose of call</span>
                    <span className={styles.labeldata}>
                      {callData?.call_purpose || "N/A"}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Related to</span>
                    <span className={styles.labeldata}>
                      {callData?.related_to || "N/A"}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Created on</span>
                    <span className={styles.labeldata}>
                      {callData?.created_date || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <hr className={styles.customDashedLine2} />

              {/* Participants Card */}
              <div className={styles.participant}>
                <h3 className={styles.cardTitle}>
                  Participants ({callData?.participants?.length})
                </h3>
                <div className={styles.participantGrid}>
                  {callData?.participants.map((p, index) => {
                    const Name = p.split("@")[0];
                    const initials = Name.split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase();
                    return (
                      <div className={styles.participantCard} key={index}>
                        <div className={styles.initialBadge}>{initials}</div>
                        <div className={styles.participantInfo}>
                          <p className={styles.participantName}>{Name}</p>
                          <p className={styles.participantEmail}>{p}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Follow-up Call Details */}
          {callData?.followup_calls && (
            <div className={styles.detailCard}>
              <div className={styles.followUpHeader}>
                <h3 className={styles.cardTitle}>Follow-up call details</h3>
                <div className={styles.followUpButtons}>
                  <button className={styles.iconButton}>
                    <img
                      className={styles.editIcon}
                      src={editIcon}
                      alt="Edit Icon"
                    />
                  </button>
                  <button className={styles.iconButton}>
                    <img
                      className={styles.deleteIcon}
                      src={deleteIcon}
                      alt="Delete Icon"
                    />
                  </button>
                </div>
              </div>
              <div className={styles.detailList}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Call type</span>
                  <span className={styles.labeldata}>
                    {callData.followup_calls.call_type}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span
                    className={styles.detailLabel}
                    style={{ marginTop: "10px" }}
                  >
                    Purpose of call
                  </span>
                  <div className={styles.callTime}>
                    <span className={styles.callTimeText}>
                      {callData.followup_calls.call_purpose || "N/A"}
                    </span>
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Reminder</span>
                  <span className={styles.reminderText}>
                    {callData.followup_calls.remind_before || "No reminder set"}
                  </span>
                </div>
              </div>
              <hr style={{ borderTop: "2px dashed #999" }} />

              {/* Remarks Section */}
              <h3 className={styles.cardTitle}>Remarks</h3>
              <p className={styles.remarksText}>
                {callData.remarks || "No remarks provided."}
              </p>
            </div>
          )}

          {callData?.teams_id && (
            <div
              style={{
                border: "1px solid #EBEBEB",
                borderRadius: "12px",
                padding: "16px",
                marginTop: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontWeight: 600,
                  fontSize: 16,
                  color: "#172B4D",
                }}
              >
                <span>AI Call Summary</span>
                <div style={{ alignItems: "center", display: "flex", gap: 10 }}>
                {aiChoices.length > 1 && (
                  <select
                    value={aiChoice}
                    onChange={(e) => setAiChoice(e.target.value)}
                    disabled={aiLoading}
                    title="Which AI summarises this call"
                    style={{
                      border: "1px solid #EBEBEB",
                      borderRadius: 12,
                      color: "#7A869A",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: 400,
                      maxWidth: 170,
                      outline: "none",
                      padding: "3px 6px",
                    }}
                  >
                    {aiChoices.map((c) => {
                      const [p, m] = c.split("|");
                      const label = { claude: "Claude", openai: "OpenAI", gemini: "Gemini" }[p] ?? p;
                      return (
                        <option key={c} value={c}>
                          {label + " · " + m}
                        </option>
                      );
                    })}
                  </select>
                )}
                <button
                  type="button"
                  onClick={handleSummariseCall}
                  disabled={aiLoading}
                  style={{
                    background: "none",
                    border: "1px solid #3D4AF5",
                    borderRadius: "24px",
                    color: "#3D4AF5",
                    cursor: aiLoading ? "default" : "pointer",
                    fontSize: "14px",
                    fontWeight: 500,
                    opacity: aiLoading ? 0.6 : 1,
                    padding: "6px 16px",
                  }}
                >
                  {aiLoading
                    ? "Reading transcript…"
                    : ai
                      ? "Regenerate"
                      : "Summarise call"}
                </button>
                </div>
              </div>

              {aiLoading && (
                <div style={{ marginTop: 12 }}>
                  <Spinner animation="border" size="sm" />
                </div>
              )}

              {!aiLoading && aiError && (
                <div style={{ color: "#CC3333", fontSize: 14, marginTop: 12 }}>
                  {aiError}
                </div>
              )}

              {!aiLoading && !aiError && !ai && (
                <div style={{ color: "#7A869A", fontSize: 14, marginTop: 12 }}>
                  Pulls the Teams transcript and extracts what was discussed,
                  what was promised, and what to do next. Transcription must
                  have been switched on during the call.
                </div>
              )}

              {!aiLoading && ai && (
                <div style={{ marginTop: 12, color: "#172B4D", fontSize: 14 }}>
                  <div style={{ lineHeight: 1.6 }}>{ai.summary}</div>
                  <div style={{ marginTop: 12 }}>
                    <strong>Commitments: </strong>
                    {ai.commitments}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <strong>Next step: </strong>
                    {ai.next_step}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallsView;
